import { type FC, type PropsWithChildren } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { FormControl, FormHelperText, Grid, TextField } from '@mui/material';

import { type EditAuditFormBackgroundData } from '#apps/client-web/pages/audit-form-background/types';
import { getFormErrorAttributes } from '#apps/client-web/app/helpers/react-hook-form';
import { FileInput } from '#apps/client-web/features/file-storage/components/file-input/file-input';

import {
  SubFormDialog,
  type SubFormDialogProps,
} from '#packages/web/layouts/dialog/templates/sub-form';
import { DeleteConfirmationDialog } from '#packages/web/layouts/dialog/templates/delete-confirmation';
import { useDialog } from '#packages/web/hooks/use-dialog';

export type DoorTypesFormDialogProps = Pick<
  SubFormDialogProps,
  'onClose' | 'open'
> & {
  control: Control<EditAuditFormBackgroundData>;
};

export const DoorTypesFormDialog: FC<
  PropsWithChildren<DoorTypesFormDialogProps>
> = ({ open, control, onClose }) => {
  const deleteAttachmentDialog = useDialog<{ proceed: () => void }>();

  return (
    <SubFormDialog title="Door types" open={open} onClose={onClose}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Controller
            control={control}
            name="envelope.doorTypes.materials"
            rules={{ maxLength: 120 }}
            render={({ field, fieldState }) => (
              <FormControl
                fullWidth
                {...getFormErrorAttributes(fieldState.error)}>
                <TextField
                  {...field}
                  label="Material"
                  type="text"
                  variant="outlined"
                  {...getFormErrorAttributes(fieldState.error)}
                />
                <FormHelperText>{fieldState.error?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Controller
            control={control}
            name="envelope.doorTypes.types"
            rules={{ maxLength: 120 }}
            render={({ field, fieldState }) => (
              <FormControl
                fullWidth
                {...getFormErrorAttributes(fieldState.error)}>
                <TextField
                  {...field}
                  label="Types"
                  type="text"
                  variant="outlined"
                  {...getFormErrorAttributes(fieldState.error)}
                />
                <FormHelperText>{fieldState.error?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Controller
            control={control}
            name="envelope.doorTypes.attachments"
            render={({ field, fieldState }) => (
              <FormControl
                fullWidth
                {...getFormErrorAttributes(fieldState.error)}>
                <FileInput
                  {...field}
                  label="Attachments"
                  onRemoveRequest={(_, proceed) =>
                    deleteAttachmentDialog.open({ proceed })
                  }
                  {...getFormErrorAttributes(fieldState.error)}
                />
                <FormHelperText>{fieldState.error?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
      </Grid>

      <DeleteConfirmationDialog
        open={deleteAttachmentDialog.isOpen}
        dialogTitle="Remove Attachment"
        confirmButtonLabel="Remove"
        onConfirm={() => {
          deleteAttachmentDialog.data?.proceed();
          deleteAttachmentDialog.close();
        }}
        onCancel={() => deleteAttachmentDialog.close()}
      />
    </SubFormDialog>
  );
};
